import { Handler } from "vega-tooltip";

/**
 * Grace period (ms) the pointer gets to travel from a chart mark onto the
 * tooltip before the tooltip is hidden.
 *
 * Longer than the 150ms the sim engine's object action menu uses
 * (interactableButtons.tsx), because that menu opens flush against its trigger
 * whereas vega-tooltip anchors itself ~10px diagonally off the cursor — and
 * WCAG 1.4.13 exists precisely for users whose pointer accuracy is reduced.
 */
export const TOOLTIP_HIDE_DELAY_MS = 300;

const DEFAULT_ELEMENT_ID = "vg-tooltip-element";
const DEFAULT_THEME = "light";

/**
 * Tells a pointer-driven call from a programmatic one.
 *
 * Vega hands the handler the DOM event it is reacting to, so a real `Event` means
 * a pointer is involved. The spreadsheet sims' keyboard access to chart marks
 * instead synthesises a plain `{clientX, clientY}` stand-in when showing, and
 * passes nothing at all when hiding - see setItemTooltip in
 * Plot/plotStatTargets.js. Both are "no pointer is travelling anywhere".
 *
 * @param {unknown} event - The second argument Vega called the handler with.
 * @returns {boolean} True only for a genuine DOM event.
 */
const isPointerDriven = (event) => event instanceof Event;

/**
 * Wraps vega-tooltip's Handler so the tooltip stays on screen while the pointer
 * moves from the chart mark onto the tooltip itself — WCAG 2.1 SC 1.4.13,
 * "Content on Hover or Focus" (Hoverable).
 *
 * Vega calls the tooltip handler with a null value the moment a mark fires
 * pointerout, and the stock Handler hides on the spot, so the content vanishes
 * before the pointer can reach it. Here that hide is deferred, and cancelled
 * outright once the pointer lands on the tooltip.
 *
 * @param {object} options - vega-tooltip Handler options.
 * @returns {{call: Function, destroy: Function}} `call` is the handler to pass
 *   to `<Vega tooltip={...} />`; `destroy` tears down listeners (tests only).
 */
export const createHoverableTooltipHandler = (options = {}) => {
  const elementId = options.id || DEFAULT_ELEMENT_ID;
  const theme = options.theme || DEFAULT_THEME;

  // Built on the first show rather than here: the Handler constructor writes a
  // `<style>` into `document.head`, and `hoverableTooltip` below is created at
  // module scope in a module the shared-components barrel re-exports, so
  // constructing eagerly would inject that stylesheet into every one of the
  // hundreds of importers that never render a chart.
  let handler = null;
  let hideTimer = null;
  let listenedElement = null;

  const getHandler = () => {
    if (!handler) {
      handler = new Handler(options);
    }
    return handler;
  };

  const getElement = () => document.getElementById(elementId);

  // Mirrors what Handler itself does for a null value, so that every hide path
  // here — timer, pointer-leave, teardown — goes through identical code.
  const hideElement = () => {
    const element = getElement();
    if (element) {
      element.classList.remove("visible", `${theme}-theme`);
    }
  };

  const cancelHide = () => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const hideNow = () => {
    cancelHide();
    hideElement();
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimer = setTimeout(() => {
      hideTimer = null;
      hideElement();
    }, TOOLTIP_HIDE_DELAY_MS);
  };

  // Decided on every show, not once: a keyboard-driven show parks the tooltip
  // directly over the plot (positionTooltipAboveMark), so leaving it
  // pointer-opaque there would stop hover reaching the marks underneath it, and
  // a mouse wandering off it would fire `mouseleave` and hide a tooltip whose
  // mark still holds focus - while usePlotStatTargets' own shownRef still
  // believed it open, so arrowing back to the same mark would not bring it back.
  //
  // The declaration carries `!important` either way, because an `!important`
  // rule in a stylesheet outranks a normal inline style: a consumer's
  // `pointer-events: none !important` would otherwise win and the tooltip would
  // vanish the instant the pointer reached it - which is exactly what the
  // spreadsheet sims' own #vg-tooltip-element rule used to do.
  const setPointerInteractive = (element, interactive) => {
    element.style.setProperty(
      "pointer-events",
      interactive ? "auto" : "none",
      "important"
    );
  };

  // vega-tooltip creates its element on the first show and shares one element
  // across every plot on the page, so listeners are attached after a show. The
  // element is tracked by identity rather than with a boolean, because
  // vega-tooltip re-creates it whenever it has been detached from the DOM (it
  // re-parents into `document.fullscreenElement`) - a flag would leave that new
  // element with no listeners and no hoverability.
  const attachListeners = (element) => {
    if (element === listenedElement) {
      return;
    }
    element.addEventListener("mouseenter", cancelHide);
    element.addEventListener("mouseleave", hideNow);
    listenedElement = element;
  };

  const call = (vegaHandler, event, item, value) => {
    if (value == null || value === "") {
      // A hide with no originating pointer event is programmatic rather than a
      // pointer leaving a mark: the spreadsheet sims' keyboard access to chart
      // marks dismisses on Escape and on focus-out by calling the installed
      // handler as (null, null, null, null). Deferring those would leave a
      // tooltip the user just dismissed on screen, and a pointer resting over it
      // would cancel the hide outright. The grace period exists for the
      // pointer's traversal, so only the pointer is given one.
      if (isPointerDriven(event)) {
        scheduleHide();
      } else {
        hideNow();
      }
      return;
    }

    cancelHide();
    getHandler().call(vegaHandler, event, item, value);

    const element = getElement();
    if (element) {
      attachListeners(element);
      setPointerInteractive(element, isPointerDriven(event));
    }
  };

  const destroy = () => {
    cancelHide();
    if (listenedElement) {
      listenedElement.removeEventListener("mouseenter", cancelHide);
      listenedElement.removeEventListener("mouseleave", hideNow);
    }
    listenedElement = null;
    hideElement();
  };

  return { call, destroy };
};

/**
 * Shared instance for the app's Vega charts. `#vg-tooltip-element` is itself a
 * singleton shared by every plot on the page, so a single wrapper owns its
 * listeners rather than each chart attaching its own.
 */
export const hoverableTooltip = createHoverableTooltipHandler();
